import { useEffect, useRef } from 'react';
import { getAllSpaceItems } from '@/db/projects';
import { getAllCachedSpaceItems, cacheSpaceItems, isIndexedDBAvailable as isStructureCacheAvailable, type CachedSpaceItems } from '@/utils/structureCache';
import type { Space } from '@/types';
import { useAppStore, type SpaceItems } from '@/store/appStore';
import { logger } from '@/utils/logger';
import { SPACE_ITEMS_CACHE_MAX_AGE_MS } from '@/constants/timings';

/**
* Hook per caricare e gestire gli item di tutti gli spaces (documenti, chat, voice channels, etc.).
* 
* Ottimizzazioni implementate:
* - Carica tutti gli spaces in parallelo invece che sequenzialmente
* - Usa cache IndexedDB per caricamento istantaneo (max 5 minuti di età)
* - Carica solo una volta per struttura di spaces, usa hash per rilevare cambiamenti
* - Aggiorna in background quando la cache si avvicina alla scadenza
* 
* Gestione cache:
* - Cache max age: 5 minuti
* - Se cache < 70% dell'età massima, usa cache e sincronizza in background
* - Se cache scaduta o non disponibile, carica da Supabase
* 
* @param spaces - Array di spaces per cui caricare gli item
* @returns Oggetto con gli item organizzati per space e le mappature delle cartelle
* @returns spaceItems - Record che mappa il nome dello space ai suoi item
* @returns folderMappings - Record che mappa il nome dello space alle mappature nome->ID delle cartelle
*/
export function useSpaceItems(spaces: Space[]) {
  const spaceItems = useAppStore((state) => state.projectItems);
  const folderMappings = useAppStore((state) => state.folderMappings);
  const setSpaceItems = useAppStore((state) => state.setSpaceItems);
  const setFolderMappings = useAppStore((state) => state.setFolderMappings);

  const spaceItemsLoadedRef = useRef(false);
  const lastSpacesHashRef = useRef<string>('');

  // Load items and folder mappings for all spaces
  // OPTIMIZED: Load all spaces in parallel instead of sequentially, and only once
  useEffect(() => {
  if (spaces.length === 0) return; // Wait for spaces
  
  // Note: spaceItems (projectItems) can be an empty object {}, which is valid
  // We only skip if spaces structure hasn't changed and all spaces already have items loaded

  // Create a hash of space IDs to detect structure changes
  const currentSpacesHash = spaces.map(s => s.id).sort().join(',');

  // Reset if spaces structure changed
  if (currentSpacesHash !== lastSpacesHashRef.current) {
      spaceItemsLoadedRef.current = false;
      lastSpacesHashRef.current = currentSpacesHash;
  }

  // Skip if already loaded and spaces structure hasn't changed
  if (spaceItemsLoadedRef.current && currentSpacesHash === lastSpacesHashRef.current) {
      // Check if we already have space items loaded for all spaces
      // Filter out spaces without name (corrupted data)
      const filteredSpaces = spaces.filter(space => space && space.name);
      const allSpacesHaveItems = spaceItems && filteredSpaces.every(space => spaceItems[space.name] !== undefined);
      
      if (allSpacesHaveItems) {
    return;
      }
      // If not all spaces have items, reset the flag to allow reloading
      spaceItemsLoadedRef.current = false;
  }

  if (spaceItemsLoadedRef.current) {
      return;
  }

  spaceItemsLoadedRef.current = true;

    const loadAllSpaceItems = async () => {
      const loadStartTime = performance.now();
      try {
    const spaceItemsMap: Record<string, SpaceItems> = {};
    const folderMappingsMap: Record<string, Record<string, string>> = {};

    // OPTIMIZATION: Use single query per space and cache for instant loading
    const itemsLoadStartTime = performance.now();

    // Try to load from cache first
    const cachedItemsMap = isStructureCacheAvailable() ? await getAllCachedSpaceItems() : new Map();

    const spaceItemPromises = spaces
          .filter(space => space && space.id && space.name) // Filter out corrupted data
          .map(async (space) => {
      const spaceId = space.id!;

      // Check cache first
      const cached: CachedSpaceItems | undefined = cachedItemsMap.get(spaceId);
      const cacheAge = cached ? Date.now() - cached.lastSyncTime : Infinity;

      // Check if cached items have parent_id
      const hasParentId = cached && cached.items ? (
              cached.items.every((item: { id: string; name: string; parent_id?: string | null }) => 'parent_id' in item)
      ) : false;

      if (cached && cacheAge < SPACE_ITEMS_CACHE_MAX_AGE_MS && hasParentId && cached.items) {
              // Use cached data directly — no Supabase fetch needed
              return {
        spaceName: space.name,
        items: cached.items as SpaceItems,
        folders: cached.folders || [],
        fromCache: true,
              };
      }

      // Fetch from Supabase using single query
      const result = await getAllSpaceItems(spaceId);

      // Cache the result
      if (isStructureCacheAvailable()) {
              await cacheSpaceItems(spaceId, {
        items: result.items.map((item) => ({
                  id: item.id,
                  name: item.name,
                  type: item.type,
                  content: item.content,
                  parent_id: item.parent_id
        })),
        folders: result.folders.map((f) => ({ id: f.id, name: f.name })),
              }).catch((error) => logger.error('Error caching space items:', error));
      }

      return {
              spaceName: space.name,
              items: result.items,
              folders: result.folders,
              fromCache: false,
      };
          });

    const spaceItemResults = await Promise.all(spaceItemPromises);
    const itemsLoadTime = performance.now() - itemsLoadStartTime;

    // Count how many were loaded from cache vs Supabase
    const fromCacheCount = spaceItemResults.filter(r => r.fromCache).length;
    const fromSupabaseCount = spaceItemResults.length - fromCacheCount;

    // Build maps from results
    for (const result of spaceItemResults) {
          spaceItemsMap[result.spaceName] = result.items;

          // Create folder name -> ID mapping
          const folderMapping: Record<string, string> = {};
          for (const folder of result.folders) {
      folderMapping[folder.name] = folder.id;
          }
          folderMappingsMap[result.spaceName] = folderMapping;
    }

    const totalTime = performance.now() - loadStartTime;
    logger.debug(`[Cache] loadAllSpaceItems: ${totalTime.toFixed(2)}ms (items loading: ${itemsLoadTime.toFixed(2)}ms, from cache: ${fromCacheCount}/${spaceItemResults.length}, from Supabase: ${fromSupabaseCount}/${spaceItemResults.length})`);

    setSpaceItems(spaceItemsMap as Record<string, SpaceItems>);
    setFolderMappings(folderMappingsMap);
      } catch (error) {
    logger.error('[Cache] Error loading space items:', error);
    spaceItemsLoadedRef.current = false; // Allow retry on error
      }
  };

  loadAllSpaceItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally not including spaceItems to avoid infinite loops
  }, [spaces, setSpaceItems, setFolderMappings]);

  return {
  spaceItems,
  folderMappings,
  };
}

